import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { isDesktop } from 'react-device-detect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { getAddressLinesFromLocation, handleLocationClick } from 'components/Calendar/utils/address';
import DeleteConfirmation from 'components/DeleteConfirmation';
import DateTimeIcon from 'assets/images/calendar/icon-datetime.svg';
import LocationIcon from 'assets/images/calendar/icon-location.svg';
import NotesIcon from 'assets/images/calendar/icon-notes.svg';
import ProjectIcon from 'assets/images/calendar/icon-project.svg';
import { EventDateTimeDisplay } from './EventDateTimeDisplay';

dayjs.extend(utc);
dayjs.extend(timezone);

const ReadOnlyEventCard = ({
  eventDetails,
  userDetails,
  project,
  onEditClick,
  onDeleteClick,
}) => {
  const {
    title: eventName,
    allDay = false,
    startStr = '',
    endStr = '',
    location = {},
    notes = '',
    displayTz,
    myTimeZone,
  } = eventDetails || {};
  const { timeFormat } = userDetails || {};
  const { name: projectName } = project || {};
  const [showDeleteConfirm, setDeleteConfirm] = useState(false);

  // If the event is all day, we always want the event to fall on the date
  // regardless of timezone. And timezone is a required field for creating a
  // project, so we can assume it will always be there.
  const start = allDay ? dayjs(startStr) : dayjs.tz(startStr, displayTz).tz(myTimeZone);
  const end = allDay ? dayjs(endStr) : dayjs.tz(endStr, displayTz).tz(myTimeZone);
  const projectDate = start ? dayjs(start).utc().format('dddd, MMMM D') : null;
  const originalOffset = useMemo(() => dayjs.tz(startStr, displayTz).utcOffset(), [startStr]);

  const whenType = allDay ? 'datespan' : 'timespan';
  const sameDay = useMemo(() => {
    // If no end string, assume it's an event that falls on the same day.
    if (!endStr || endStr === '') return true;
    if (whenType === 'timespan') return start.isSame(end, 'day');
    // For all day events, end time is the next day but at 00:00:00, so subtract
    // 1 day to see if it's the same day.
    return end.subtract(1, 'day').isSame(start, 'day');
  }, [whenType]);

  const addressLines = useMemo(() => getAddressLinesFromLocation(location), [location]);
  const hasAddress = addressLines.addressLine1 || addressLines.addressLine2 || addressLines.formattedAddress;

  return (
    <div className="event-card info-card">
      <div className="info-card__content">
        <div className="info-card__row">
          <div className="info-card__title ap-text">
            {eventName}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={DateTimeIcon}
              alt="date-time icon"
            />
          </div>
          <div>
            {projectDate ? (
              <EventDateTimeDisplay
                start={start}
                end={end}
                timeFormat={timeFormat}
                whenType={whenType}
                sameDay={sameDay}
                originalOffset={originalOffset}
                displayTz={displayTz}
              />
            ): '-'}
          </div>
        </div>

        <div className="info-card__row">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={ProjectIcon}
              alt="project icon"
            />
          </div>
          <div>{projectName || '-'}</div>
        </div>

        <div className="info-card__row"
          style={{ ... hasAddress && { position: 'relative' } }}
        >
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={LocationIcon}
              alt="location icon"
            />
          </div>
          <div
            onClick={() => handleLocationClick(addressLines, location, isDesktop)}
            style={{
              ... hasAddress && { cursor: 'pointer' } }}
          >
            {addressLines.addressLine1 && (
              <div className="info-card__formatted-address">
                <div>
                  {addressLines.addressLine1}
                  {addressLines.addressLine2 ? ',' : ''}
                </div>
                {addressLines.addressLine2 && <div>{addressLines.addressLine2}</div>}
              </div>
            )}
            {(!addressLines.addressLine1 && location.formattedAddress) && (
              <div className="info-card__formatted-address">
                {location.formattedAddress}
              </div>
            )}
            {(!addressLines.addressLine1 && !location.formattedAddress) && (
              <>-</>
            )}
            {hasAddress && (
              <FontAwesomeIcon
                icon={['fa', 'angle-right']}
                className="click-indicator"
              />
            )}
          </div>
        </div>

        <div className="info-card__row info-card__row--text">
          <div className="info-card__icon-wrapper">
            <img
              className="info-card__icon"
              src={NotesIcon}
              alt="notes icon"
            />
          </div>
          <div>{notes || '-'}</div>
        </div>
      </div>

      <div className="button-group">
        <button
          className="button--unstyled gold--large"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteConfirm(true);
          }}
        >
          Delete
        </button>
        <button
          className="button--unstyled gold--large"
          onClick={onEditClick}
        >
          Edit
        </button>

        <DeleteConfirmation
          show={showDeleteConfirm}
          handleClose={() => {
            setDeleteConfirm(false);
          }}
          handleConfirm={() => {
            onDeleteClick();
            setDeleteConfirm(false);
          }}
          deleteText="DELETE EVENT"
          size="xs"
        >
          <p>
            Deleting this calendar event will remove<br />
            it from your <b>account</b> forever.<br />
            Are you sure?
          </p>
        </DeleteConfirmation>
      </div>
    </div>
  );
};

export default ReadOnlyEventCard;
